import { z } from 'zod';
import { MUSIC_DEFAULT_LEVEL } from './RoomMusic.js';

export const roomMusicDefaultsSchema = z.object({
  enabled: z.boolean(),
  defaultPlaylistId: z.string().uuid().nullable(),
  level: z.number().finite().min(0).max(1),
  autoStart: z.boolean(),
  /** Spoken music commands (#4278); the panel is unaffected. Absent means on, so rooms saved before the switch existed keep their bytes. */
  voiceControl: z.boolean().optional(),
}).strict().refine(value => !value.autoStart || value.defaultPlaylistId !== null, {
  message: 'Choose a default playlist before enabling automatic start.', path: ['defaultPlaylistId'],
});
export type RoomMusicDefaults = z.infer<typeof roomMusicDefaultsSchema>;
export const DEFAULT_ROOM_MUSIC: Readonly<RoomMusicDefaults> = {
  enabled: true, defaultPlaylistId: null, level: MUSIC_DEFAULT_LEVEL, autoStart: false,
};

/** Old workflows need no migration. Preserve an explicit disable even if another field is malformed. */
export function readRoomMusicDefaults(configuration: unknown): RoomMusicDefaults {
  const value = configuration && typeof configuration === 'object'
    ? (configuration as { music?: Partial<RoomMusicDefaults> }).music : undefined;
  const defaultPlaylistId = z.string().uuid().safeParse(value?.defaultPlaylistId);
  return {
    enabled: value?.enabled !== false,
    defaultPlaylistId: defaultPlaylistId.success ? defaultPlaylistId.data : null,
    level: typeof value?.level === 'number' && Number.isFinite(value.level) && value.level >= 0 && value.level <= 1 ? value.level : MUSIC_DEFAULT_LEVEL,
    autoStart: value?.autoStart === true && defaultPlaylistId.success,
    // Only an explicit off is carried; on is the absence of the field.
    ...(value?.voiceControl === false ? { voiceControl: false } : {}),
  };
}
